import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logger } from "@/lib/logger";

import { MicrosoftLogo } from "./microsoft-logo";
import { ssoErrorMessage } from "./sso-errors";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Spare Parts ID";

// Root-relative on purpose: /api/* is proxied by Vite in dev and nginx in prod.
// This must be a real browser navigation — the endpoint 302s to Microsoft, which
// XHR cannot follow, so it is never a fetch/TanStack Query call.
const SSO_LOGIN_URL = "/api/auth/sso/login";

export function LoginPage() {
  const [searchParams] = useSearchParams();
  // The only error this page can show. The callback bounces failures back here
  // as /login?error=<code>; there is no longer a local form that can fail.
  const error = ssoErrorMessage(searchParams.get("error"));

  return (
    <div className="from-background via-accent-light/40 to-background flex min-h-screen items-center justify-center bg-gradient-to-br px-4 py-10">
      <Card className="w-full max-w-5xl overflow-hidden border-0 shadow-2xl">
        <div className="grid md:grid-cols-[1.15fr_0.85fr]">
          <div className="from-accent to-accent/90 text-accent-foreground hidden flex-col justify-between bg-gradient-to-br p-10 md:flex">
            <div className="space-y-6">
              {/* Logo is a dark mark meant for light surfaces; invert it to a
                  white silhouette so it reads on this dark navy panel. */}
              <img
                src="/brand/logo.png"
                alt="Norican"
                className="h-10 w-auto brightness-0 invert"
              />
              <div className="space-y-4">
                <p className="text-sm font-semibold tracking-[0.24em] text-white/70 uppercase">
                  Field service AI
                </p>
                <h1 className="font-heading max-w-md text-4xl font-semibold">
                  Identify the right spare Part ID from service context in minutes.
                </h1>
                <p className="max-w-lg text-sm leading-6 text-white/80">
                  Built for foundry and surface preparation teams to upload reports, images and
                  machine details, then search internal knowledge with AI-guided precision.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-2xl font-semibold text-white">AI</p>
                <p className="text-white/70">Knowledge search</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">Fast</p>
                <p className="text-white/70">Part identification</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">Global</p>
                <p className="text-white/70">Technician ready</p>
              </div>
            </div>
          </div>
          <div className="bg-card flex flex-col justify-center">
            <CardHeader className="space-y-3 px-8 pt-8 text-center md:px-10 md:pt-10">
              <img src="/brand/logo.png" alt="Norican" className="mx-auto h-10 w-auto md:hidden" />
              <p className="text-primary text-xs font-semibold tracking-[0.24em] uppercase">
                {PROJECT_NAME}
              </p>
              <CardTitle className="font-heading text-foreground text-3xl font-semibold">
                Sign in
              </CardTitle>
              <p className="text-muted text-sm">
                Access service intelligence for machine spare parts
              </p>
            </CardHeader>
            <CardContent className="space-y-4 px-8 pb-8 md:px-10 md:pb-10">
              <Button asChild variant="outline" className="w-full">
                <a
                  href={SSO_LOGIN_URL}
                  onClick={() => logger.debug("[login] starting Entra ID SSO")}
                >
                  <MicrosoftLogo className="h-4 w-4" />
                  Sign in with Microsoft
                </a>
              </Button>
              {error && (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              )}
              <p className="text-muted text-center text-sm">
                Use your Norican work account. Access is managed by your IT administrator.
              </p>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
}
